import { el, clear } from "./dom.js";

const PLACEHOLDER = "/placeholder-cover.svg";

// Inline, multi-select editions picker. Follows listPicker's inline-panel idiom,
// but loads items asynchronously in pages (a work/game can have hundreds of
// editions) and toggles selection live — tapping a row immediately marks that
// edition owned/not via onToggle, matching how the rest of the app's chips and
// group picker apply changes instantly.
//
// Because the upstream APIs can't search *within* editions, the filter box only
// narrows what's already been loaded; "Load more" pulls the next page.
//
// Options:
//   loadPage({ offset })  async → { editions, next_offset }
//   idOf(edition)         unique key for an edition
//   renderRow(edition)    → { title, sub?, cover_url? } for the row
//   isSelected(edition)   → bool, whether it's currently owned
//   onToggle(edition, nowSelected)  called when a row is tapped
//
// Returns the panel node (hidden by default). Call node.ensureLoaded() when you
// reveal it to trigger the first fetch.
export function editionsPicker({
	loadPage,
	idOf,
	renderRow,
	isSelected,
	onToggle,
	placeholder = "Filter loaded editions…",
}) {
	const root = el("div.picker.ed-picker", { hidden: true });

	const input = el("input.picker__input", {
		type: "search",
		placeholder,
		autocomplete: "off",
		autocapitalize: "off",
		spellcheck: false,
	});
	const list = el("div.picker__list.ed-picker__list", {});
	const status = el("div.ed-picker__status", {});

	let loaded = [];
	let nextOffset = 0;
	let loading = false;
	let started = false;
	let errored = false;

	function matches(edition, norm) {
		if (!norm) return true;
		const { title, sub } = renderRow(edition);
		return `${title ?? ""} ${sub ?? ""}`.toLowerCase().includes(norm);
	}

	// A cover-only grid tile (title is the tooltip/alt; the year/publisher line is
	// intentionally dropped — the cover is the picker now). Selected tiles get a
	// lime ring + a check badge.
	function tileFor(edition) {
		const { title, cover_url } = renderRow(edition);

		const check = el("span", {
			style: {
				position: "absolute", top: "5px", right: "5px", width: "20px", height: "20px",
				borderRadius: "50%", background: "#b6f23d", color: "#0b0b0f",
				display: isSelected(edition) ? "flex" : "none", alignItems: "center", justifyContent: "center",
				fontSize: "12px", fontWeight: "900", boxShadow: "0 2px 6px rgba(0,0,0,.4)",
			},
		}, "✓");

		const tile = el("div", {
			role: "button",
			title: title || "Untitled edition",
			"aria-pressed": isSelected(edition) ? "true" : "false",
			onclick: () => {
				const now = !isSelected(edition);
				onToggle(edition, now);
				tile.style.outline = now ? "2px solid #b6f23d" : "1px solid rgba(255,255,255,.12)";
				check.style.display = now ? "flex" : "none";
				tile.setAttribute("aria-pressed", now ? "true" : "false");
			},
			style: {
				position: "relative", aspectRatio: "2 / 3", borderRadius: "8px", overflow: "hidden",
				cursor: "pointer", background: "#1a1a20",
				outline: isSelected(edition) ? "2px solid #b6f23d" : "1px solid rgba(255,255,255,.12)",
				outlineOffset: "-1px",
			},
		},
			el("img", {
				src: cover_url || PLACEHOLDER,
				alt: title || "",
				loading: "lazy",
				onerror: (e) => { e.currentTarget.src = PLACEHOLDER; },
				style: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
			}),
			check,
		);
		return tile;
	}

	function renderList() {
		clear(list);
		const norm = input.value.trim().toLowerCase();
		const filtered = loaded.filter((e) => matches(e, norm));

		if (filtered.length === 0) {
			list.append(
				el(
					"div.picker__empty",
					{},
					loaded.length === 0
						? errored
							? "Couldn't load editions."
							: "No editions found."
						: "No matches in what's loaded — try Load more.",
				),
			);
		} else {
			const grid = el("div", {
				style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" },
			});
			for (const edition of filtered) grid.append(tileFor(edition));
			list.append(grid);
		}

		if (nextOffset != null && !errored) {
			list.append(
				el(
					"button.btn.btn--small.ed-picker__more",
					{ type: "button", disabled: loading, onclick: () => loadNext(), style: { marginTop: "10px" } },
					loading ? "Loading…" : "Load more",
				),
			);
		}
	}

	async function loadNext() {
		if (loading || nextOffset == null) return;
		loading = true;
		errored = false;
		status.textContent = loaded.length === 0 ? "Loading editions…" : "";
		renderList();
		try {
			const { editions = [], next_offset = null } = await loadPage({
				offset: nextOffset,
			});
			// De-dupe in case pages overlap.
			const seen = new Set(loaded.map(idOf));
			for (const e of editions) {
				if (!seen.has(idOf(e))) loaded.push(e);
			}
			nextOffset = next_offset;
		} catch {
			errored = true;
			nextOffset = null;
		} finally {
			loading = false;
			status.textContent = "";
			renderList();
		}
	}

	input.addEventListener("input", renderList);

	root.ensureLoaded = () => {
		if (started) return;
		started = true;
		loadNext();
	};
	// Re-render rows from current selection state (e.g. after a chip is removed
	// outside the picker while it's open).
	root.refresh = renderList;

	root.append(input, status, list);
	renderList();
	return root;
}

// The full "Editions you own" detail section: a label, removable chips for the
// editions already owned, and a toggle that reveals the async picker. Shared by
// book-detail and game-detail so both behave identically; the caller supplies the
// media-specific loader, describe(), and persist().
//
//   owned       initial array of owned edition snapshots (from the record)
//   loadPage    ({offset}) => { editions, next_offset }
//   describe    edition|snapshot => { title, sub?, cover_url? }
//   persist     async (ownedArray) => void   (calls the sanitizing setter)
export function ownedEditionsSection({
	owned,
	loadPage,
	describe,
	persist,
	addLabel = "+ Choose editions",
}) {
	const idOf = (e) => String(e.edition_id);
	let current = Array.isArray(owned) ? owned.slice() : [];

	const wrap = el(
		"div.field",
		{},
		el("label.field__label", {}, "Editions you own"),
	);
	const chipsWrap = el("div.chips.ed-chips", {});
	wrap.append(chipsWrap);

	function isSelected(edition) {
		return current.some((o) => idOf(o) === idOf(edition));
	}

	function renderChips() {
		clear(chipsWrap);
		if (current.length === 0) {
			chipsWrap.append(
				el(
					"div.detail__hint",
					{},
					el("span", {}, "No specific editions picked yet."),
				),
			);
			return;
		}
		for (const ed of current) {
			const { title, sub } = describe(ed);
			const chip = el(
				"div.chip.chip--active.ed-chip",
				{},
				el("span.chip__label", {}, sub ? `${title} · ${sub}` : title),
			);
			const remove = el(
				"button.chip__remove",
				{
					type: "button",
					title: "Remove edition",
					onclick: async (e) => {
						e.stopPropagation();
						current = current.filter((o) => idOf(o) !== idOf(ed));
						await persist(current);
						renderChips();
						picker.refresh();
					},
				},
				"×",
			);
			chip.append(remove);
			chipsWrap.append(chip);
		}
	}

	const picker = editionsPicker({
		loadPage,
		idOf,
		renderRow: (e) => describe(e),
		isSelected,
		onToggle: async (edition, now) => {
			if (now) {
				if (!isSelected(edition)) current.push(edition);
			} else {
				current = current.filter((o) => idOf(o) !== idOf(edition));
			}
			await persist(current);
			renderChips();
		},
	});

	const toggleBtn = el(
		"button.btn.btn--small",
		{
			type: "button",
			onclick: () => {
				picker.hidden = !picker.hidden;
				if (!picker.hidden) {
					picker.ensureLoaded();
					const inp = picker.querySelector(".picker__input");
					if (inp) queueMicrotask(() => inp.focus());
				}
			},
		},
		addLabel,
	);

	wrap.append(toggleBtn, picker);
	renderChips();
	return wrap;
}
